/**
 * Modern Cart Enhancement
 * Provides enhanced cart interactions with accessibility and loading states
 */

(function() {
  'use strict';

  // Wait for DOM to be ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  function init() {
    const cart = document.querySelector('.CartModern');
    if (!cart) return;

    const aria = cart.querySelector('[data-cart-aria]');
    if (!aria) {
      // Create aria-live region if it doesn't exist
      const ariaRegion = document.createElement('div');
      ariaRegion.className = 'visually-hidden';
      ariaRegion.setAttribute('aria-live', 'polite');
      ariaRegion.setAttribute('data-cart-aria', '');
      cart.appendChild(ariaRegion);
    }

    // Initialize sticky offset calculation
    setStickyOffset();
    window.addEventListener('resize', debounce(setStickyOffset, 250));

    // Bind event listeners
    bindEventListeners(cart);
  }

  /**
   * Calculate and set sticky offset for summary
   */
  function setStickyOffset() {
    const cart = document.querySelector('.CartModern');
    if (!cart) return;

    const header = document.querySelector('header, .shopify-section-header, .header');
    const headerHeight = header ? header.getBoundingClientRect().height : 0;
    const offset = headerHeight + 16;
    
    cart.style.setProperty('--cart-sticky-offset', `${offset}px`);
  }

  /**
   * Bind event listeners for cart interactions
   */
  function bindEventListeners(cart) {
    // Quantity controls
    cart.addEventListener('click', handleQuantityClick);
    
    // Remove buttons
    cart.addEventListener('click', handleRemoveClick);
    
    // Form submission
    const form = cart.querySelector('form[action*="/cart"]');
    if (form) {
      form.addEventListener('submit', handleFormSubmit);
    }
  }

  /**
   * Handle quantity increment/decrement clicks
   */
  function handleQuantityClick(event) {
    const button = event.target.closest('[data-dec], [data-inc]');
    if (!button) return;

    event.preventDefault();
    
    const lineItem = button.closest('.LineItem');
    if (!lineItem) return;

    const input = lineItem.querySelector('input[type="number"]');
    if (!input) return;

    const isDecrement = button.hasAttribute('data-dec');
    let quantity = parseInt(input.value, 10) || 1;
    
    if (isDecrement) {
      quantity = Math.max(0, quantity - 1);
    } else {
      quantity = quantity + 1;
    }

    updateLineItem(lineItem, quantity);
  }

  /**
   * Handle remove button clicks
   */
  function handleRemoveClick(event) {
    const button = event.target.closest('[data-remove]');
    if (!button) return;

    event.preventDefault();
    
    const lineItem = button.closest('.LineItem');
    if (!lineItem) return;

    updateLineItem(lineItem, 0);
  }

  /**
   * Handle form submission (fallback for non-JS users)
   */
  function handleFormSubmit(event) {
    // Let the form submit normally for non-JS users
    // This is just a placeholder for any additional form handling
  }

  /**
   * Update a line item quantity
   */
  async function updateLineItem(lineItem, quantity) {
    const key = lineItem.dataset.key;
    if (!key) {
      console.error('Line item missing data-key attribute');
      return;
    }

    // Set loading state
    setLoadingState(lineItem, true);
    
    try {
      const response = await fetch('/cart/change.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: JSON.stringify({
          id: key,
          quantity: quantity
        })
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const cart = await response.json();
      
      if (quantity === 0) {
        // Remove the line item from DOM
        lineItem.remove();
        announceUpdate('Item removed from cart');
        
        // Check if cart is now empty
        const remainingItems = document.querySelectorAll('.LineItem');
        if (remainingItems.length === 0) {
          showEmptyCartState();
        }
      } else {
        // Update the line item
        updateLineItemDOM(lineItem, cart.items.find(item => item.key === key));
        announceUpdate(`Quantity updated to ${quantity}`);
      }

      // Update summary
      updateSummary(cart);
      
      // Trigger cart update event for other scripts
      document.dispatchEvent(new CustomEvent('cart:updated', {
        detail: { cart, lineItem, quantity }
      }));

    } catch (error) {
      console.error('Cart update failed:', error);
      showError(lineItem, 'Update failed. Please try again.');
      announceUpdate('Cart update failed');
    } finally {
      setLoadingState(lineItem, false);
    }
  }

  /**
   * Update line item DOM with new data
   */
  function updateLineItemDOM(lineItem, itemData) {
    if (!itemData) return;

    // Update quantity input
    const input = lineItem.querySelector('input[type="number"]');
    if (input) {
      input.value = itemData.quantity;
    }

    // Update price
    const priceElement = lineItem.querySelector('.LineItem__price');
    if (priceElement && itemData.line_price) {
      priceElement.textContent = formatMoney(itemData.line_price);
    }

    // Update quantity display
    const qtyDisplay = lineItem.querySelector('.quantities-option');
    if (qtyDisplay) {
      qtyDisplay.textContent = itemData.quantity;
    }
  }

  /**
   * Update cart summary with new totals
   */
  function updateSummary(cart) {
    // Update subtotal
    const subtotalElement = document.querySelector('[data-cart-subtotal]');
    if (subtotalElement && cart.total_price) {
      subtotalElement.textContent = formatMoney(cart.total_price);
    }

    // Update item count
    const countElement = document.querySelector('[data-cart-count]');
    if (countElement) {
      countElement.textContent = cart.item_count;
    }

    // Update total price in summary
    const totalElement = document.querySelector('.SummaryItem--total .SummaryItem__value');
    if (totalElement && cart.total_price) {
      totalElement.textContent = formatMoney(cart.total_price);
    }
  }

  /**
   * Show empty cart state when all items are removed
   */
  function showEmptyCartState() {
    const cart = document.querySelector('.CartModern');
    if (!cart) return;

    // Hide the cart grid and summary
    const cartGrid = cart.querySelector('.CartModern__grid');
    if (cartGrid) {
      cartGrid.style.display = 'none';
    }

    // Show empty cart state
    const emptyState = cart.querySelector('.CartModern__empty');
    if (emptyState) {
      emptyState.style.display = 'block';
    } else {
      // Create empty state if it doesn't exist
      const emptyStateHTML = `
        <div class="CartModern__empty">
          <h2>Your cart is empty</h2>
          <p>Continue shopping to add items to your cart.</p>
          <a href="/collections/all" class="Button">Continue Shopping</a>
        </div>
      `;
      cart.insertAdjacentHTML('beforeend', emptyStateHTML);
    }

    announceUpdate('Cart is now empty');
  }

  /**
   * Show cart grid when items are present
   */
  function showCartGrid() {
    const cart = document.querySelector('.CartModern');
    if (!cart) return;

    // Show the cart grid and summary
    const cartGrid = cart.querySelector('.CartModern__grid');
    if (cartGrid) {
      cartGrid.style.display = 'grid';
    }

    // Hide empty cart state
    const emptyState = cart.querySelector('.CartModern__empty');
    if (emptyState) {
      emptyState.style.display = 'none';
    }
  }

  /**
   * Set loading state for a line item
   */
  function setLoadingState(lineItem, isLoading) {
    const controls = lineItem.querySelectorAll('button, input');
    
    controls.forEach(control => {
      control.setAttribute('aria-disabled', isLoading);
      control.disabled = isLoading;
    });

    if (isLoading) {
      lineItem.classList.add('LineItem__loading');
    } else {
      lineItem.classList.remove('LineItem__loading');
    }
  }

  /**
   * Show error message for a line item
   */
  function showError(lineItem, message) {
    // Remove existing error
    const existingError = lineItem.querySelector('.LineItem__error');
    if (existingError) {
      existingError.remove();
    }

    // Add new error
    const errorElement = document.createElement('div');
    errorElement.className = 'LineItem__error';
    errorElement.textContent = message;
    
    const content = lineItem.querySelector('.LineItem__content');
    if (content) {
      content.appendChild(errorElement);
    }

    // Remove error after 5 seconds
    setTimeout(() => {
      if (errorElement.parentNode) {
        errorElement.remove();
      }
    }, 5000);
  }

  /**
   * Announce updates to screen readers
   */
  function announceUpdate(message) {
    const aria = document.querySelector('[data-cart-aria]');
    if (aria) {
      aria.textContent = message;
      // Clear after announcement
      setTimeout(() => {
        aria.textContent = '';
      }, 1000);
    }
  }

  /**
   * Format money using Shopify's format
   */
  function formatMoney(cents) {
    if (typeof window.Shopify !== 'undefined' && window.Shopify.formatMoney) {
      return window.Shopify.formatMoney(cents);
    }
    
    // Fallback formatting
    const amount = (cents / 100).toFixed(2);
    return `€${amount}`;
  }

  /**
   * Debounce function to limit function calls
   */
  function debounce(func, wait) {
    let timeout;
    return function executedFunction(...args) {
      const later = () => {
        clearTimeout(timeout);
        func(...args);
      };
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
    };
  }

  /**
   * Handle cart drawer/modal updates (if theme uses them)
   */
  function handleCartDrawerUpdate(event) {
    // This would be called when cart drawer is updated
    // Implementation depends on theme's cart drawer system
    const cart = document.querySelector('.CartModern');
    if (cart) {
      // Refresh cart data if needed
      fetch('/cart.js')
        .then(response => response.json())
        .then(cartData => {
          updateSummary(cartData);
          
          // Check if cart is empty or has items
          if (cartData.item_count === 0) {
            showEmptyCartState();
          } else {
            showCartGrid();
          }
        })
        .catch(error => {
          console.error('Failed to refresh cart:', error);
        });
    }
  }

  // Listen for cart drawer updates
  document.addEventListener('cart:drawer-updated', handleCartDrawerUpdate);

  // Export functions for external use if needed
  window.CartModern = {
    updateLineItem,
    updateSummary,
    announceUpdate,
    formatMoney
  };

})();
